<script setup>
import { onMounted, computed, ref, nextTick, onBeforeUnmount } from "vue";
import ArticleCard from "~/components/ArticleCard.vue";
import gsap from "gsap"
import ScrollToPlugin from "gsap/ScrollToPlugin";

const localePath = useLocalePath();
const isMobile = ref(false);

const props = defineProps({
  articles: Array,
  heading: String,
  setCursor: {
    type: Function,
    required: true,
  },
});

function updateIsMobile() {
  isMobile.value = window.innerWidth < 1124
}

const articleLinks = ref([
  { title: "Yodas", link: "https://www.yodas.opero.cz/" }
]);

const settings = ref({
  itemsToShow: 1.5,
  snapAlign: "center",
});

const breakpoints = ref({
  500: {
    itemsToShow: 2,
    snapAlign: "center",
  },
  765: {
    itemsToShow: 2,
    snapAlign: "center",
  },
  1124: {
    itemsToShow: 2.5,
    snapAlign: "start",
  },
  1300: {
    itemsToShow: 3.3,
    snapAlign: "start",
  },
});

const currentSlide = ref(0);

const { articles } = props;
const testimonialContainer2 = ref();

function trimPerex(perex, wordLimit = 20) {
  if (!perex) return ''
  const words = perex.split(/\s+/)
  if (words.length <= wordLimit) return perex
  return words.slice(0, wordLimit).join(' ') + '...'
}

const processedArticles = computed(() =>
  (props.articles || []).map(article => {
    const copy = JSON.parse(JSON.stringify(article.node));
    const _title = copy.title.split(':')
    let title = ''

    if (_title.length > 1) {
      title = `<strong>${_title[0]}:</strong> `
      _title.shift()
      title += _title.join(':')
    } else {
      title = _title.join(':')
    }

    copy.featuredImage = article?.node?.featuredImage?.node?.mediaItemUrl || null
    copy.perex = article?.node?.blogConfig?.perex
    copy.title = title
    copy.isInitialSlide = false
    return copy
  })
)

onMounted(() => {
  updateIsMobile();
  window.addEventListener('resize', updateIsMobile);

  gsap.registerPlugin(ScrollToPlugin);

  nextTick(() => {
    const wrapper = testimonialContainer2.value?.firstElementChild;
    if (wrapper) {
      wrapper.style.display = "flex";
      wrapper.id = "draggable-wrapper";
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIsMobile)
})

function scrollTo() {
  if (process.client) {
    gsap.to(window, { duration: 1, scrollTo: "#bubbles" });
  }
}

// const visibleSlides = computed(() =>
//   processedArticles.value.filter(article => !(article.isInitialSlide && isMobile.value))
// )
const visibleSlides = computed(() => {
  const slides = [...processedArticles.value]

  if (props.heading) {
    slides.unshift({
      title: props.heading.replace('__', '<br>'),
      isInitialSlide: true
    })
  }

  // filter for mobile
  return slides.filter(slide => !(slide.isInitialSlide && isMobile.value))
})
</script>

<template>
  <div
    class="lg:flex justify-center article-carousel"
  >
    <div class="max-w-7xl projects-home">

      <Carousel
        ref="carousel"
        v-bind="settings"
        v-model="currentSlide"
        :breakpoints="breakpoints"
        class="testimonial-container projects-home"
        
      >
        <Slide
          v-for="(article, i) in visibleSlides"
          :key="article.id"
        >
          <div v-if="article.isInitialSlide" class="article-carousel-heading p-3">
            <div class="article-carousel-heading__inner">
              <h2 v-html="article.title" class="text-3xl font-bold"></h2>
            </div>
          </div>

          <template v-else>
            <NuxtLinkLocale
              v-if="articleLinks.find((p) => p.title === article?.title)"
              :to="articleLinks.find((p) => p.title === article?.title)['link']"
            >
              <ArticleCard
                :set-cursor="setCursor"
                class="p-3 panel-projects"
                :article="article"
                :show-date="false"
                :show-button="false"
              />
            </NuxtLinkLocale>
            <ArticleCard
              v-else
              :set-cursor="setCursor"
              class="p-3 panel-projects"
              :article="article"
              :show-date="false"
              :show-button="false"
            />
          </template>
        </Slide>
				<template #addons>
          <Navigation />
        </template>
      </Carousel>
      <div class="px-6 md:hidden">
        <div
          class="w-full bg-gray-200 rounded-full h-1 dark:bg-gray-700 mt-4 mb-12"
        >
          <div
            class="bg-blue-600 h-1 rounded-full"
            :style="{
              width: `${
                ((Math.ceil(currentSlide) + 1) * 100) / articles?.length
              }%`,
              transition: 'width 0.3s',
              background: '#FFC800',
            }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.article-carousel {
  &-heading {
    width: 100%;

    &__inner {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      background: #ffc800;
    }

    h2 {
      max-width: 70%;
      margin: 0 auto;
      line-height: 1.4em;
    }
  }
}

.background-bubble {
  background: url("assets/Union.png");
  background-repeat: no-repeat;
}

.draggable-wrapper:first-child {
  padding-right: 0;
}

.testimonial-container2::-webkit-scrollbar {
  display: none;
}

.projects-home .carousel__slide {
  align-items: unset;
}

.projects-home {
  width: 100%;
}

.testimonial-container:deep(.carousel__prev) {
	top: 25%;

  @media all and (min-width: 1124px) {
    background: #fff;
  }

	@media screen and (min-width: 1500px) {
    left: -95px;
		
  }
}
.testimonial-container:deep(.carousel__next) {
	top: 25%;
	@media screen and (min-width: 1500px) {
    right: -95px;
		
  }
}

.carousel__next {
  background: #ffc800;
  border-radius: 100%;

  &:hover {
    background: #ffc800;
  }
}
.carousel__prev {
  background: #ffc800;
  border-radius: 100%;
	left: -95px;

  &:hover {
    background: #ffc800;
  }
}

.carousel__icon {
  height: 20px !important;
}
</style>